import type { QueryClient } from '@tanstack/react-query';

import type { AppShellApiClient } from '../shared/types';
import createQueryConfig, { NotFoundError } from '../queries';
import {
  parseAppointmentUsage,
  type AppointmentUsage,
} from '../features/bookings-upgrade/usage';
import createDebug from './debug';

const debug = createDebug('fetch-appointment-usage');

/**
 * Appointment allowance and spend for the current merchant, or `null` when the
 * merchant has none.
 *
 * Unlike `fetchSubscription`, this never rejects. It runs during bootstrap and
 * everything it feeds is an upsell: a 404 (not a WSB merchant), a rejected
 * token and a changed response shape all mean the same thing here — no card, no
 * dialog — and none of them is a reason to fail the shell's startup.
 *
 * Pass `force` when the caller knows something changed since any request that
 * may already be open. `fetchQuery` hands back an in-flight request for the same
 * key rather than issuing a new one, so without this a second appointment
 * landing mid-read resolves with a count taken before it existed — and nothing
 * re-reads after that. Cancelling first is the only way to reach `cancelRefetch`
 * through this API. The cancelled caller gets `null` and the forcing one gets
 * the truth.
 */
export async function fetchAppointmentUsage(
  merchantId: string,
  apiClient: AppShellApiClient,
  queryClient: QueryClient,
  { force = false }: { force?: boolean } = {}
): Promise<AppointmentUsage | null> {
  try {
    const queryConfig = createQueryConfig(queryClient, apiClient);
    const { queryKey, queryFn } = queryConfig.appointmentUsage({ merchantId });

    if (force) await queryClient.cancelQueries({ queryKey, exact: true });

    const response = await queryClient.fetchQuery({ queryKey, queryFn });

    return parseAppointmentUsage(response);
  } catch (error) {
    // A 404 is the documented answer for a merchant with no allowance, not a
    // fault, so it is not worth a line even with debug logging switched on.
    if (error instanceof NotFoundError) return null;

    // Message only, never the error object: every authenticated query attaches
    // its bearer token to the rejected error (see injectErrorMetadata in
    // ../queries), and `debug` writes through console, where anything logged
    // can be picked up as a Sentry breadcrumb.
    debug(
      error instanceof Error ? `${error.name}: ${error.message}` : String(error)
    );
    return null;
  }
}
